import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import { 
  Plus, Trash2, GripVertical, Save, ArrowLeft, Eye, Rocket,
  ChevronUp, ChevronDown, Copy, Settings2, FileText, Upload
} from 'lucide-react';
import { logger } from '../utils/logger';
import { OptionListEditor } from '../components/OptionListEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkbox(es)' },
];

const OPTION_TYPES = ['dropdown', 'radio', 'checkbox'];

const FILLED_BY_OPTIONS = [
  { value: 'OFFICE', label: 'Office Only' },
  { value: 'CLIENT', label: 'Client' },
  { value: 'BOTH', label: 'Both' },
];

const VISIBILITY_OPTIONS = [
  { value: 'visible', label: 'Visible to Client' },
  { value: 'hidden', label: 'Hidden from Client' },
];

export default function TemplateBuilderPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!templateId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [tags, setTags] = useState([]);
  
  // Template data
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [sections, setSections] = useState([]);
  const [declaration, setDeclaration] = useState({
    declaration_text: '',
    require_ack_checkbox: true,
    require_signature: false
  });
  const [requiredUploads, setRequiredUploads] = useState([]);
  const [settings, setSettings] = useState({
    default_expiry_days: 14,
    use_global_branding: true
  });
  
  // UI state
  const [activeSection, setActiveSection] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [optionsError, setOptionsError] = useState('');
  const [isFieldDialogOpen, setIsFieldDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [editingUpload, setEditingUpload] = useState(null);

  const fetchTags = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/booking-forms/tags`, { credentials: 'include' });
      if (response.ok) {
        setTags(await response.json());
      }
    } catch (error) {
      logger.error('Error fetching tags:', error);
    }
  }, []);

  const fetchTemplate = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/booking-forms/templates/${templateId}`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch template');
      
      const template = await response.json();
      setTemplateName(template.name);
      setTemplateDescription(template.description || '');
      setSelectedTags(template.category_tags || []);
      
      const schema = template.schema_json || {};
      setSections(schema.sections || []);
      setDeclaration(schema.declaration || { declaration_text: '', require_ack_checkbox: true, require_signature: false });
      setRequiredUploads(schema.required_uploads || []);
      setSettings(schema.settings || { default_expiry_days: 14, use_global_branding: true });
    } catch (error) {
      toast.error(error.message);
      navigate('/booking-forms');
    } finally {
      setLoading(false);
    }
  }, [templateId, navigate]);

  useEffect(() => {
    fetchTags();
    if (isEditMode) {
      fetchTemplate();
    }
  }, [fetchTags, fetchTemplate, isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateId = (prefix) => `${prefix}_${Math.random().toString(36).substr(2, 9)}`;

  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error('Template name is required');
      return;
    }

    setSaving(true);
    try {
      const schemaJson = {
        sections,
        declaration,
        required_uploads: requiredUploads,
        settings
      };

      const payload = {
        name: templateName,
        description: templateDescription,
        category_tags: selectedTags,
        schema_json: schemaJson
      };

      const url = isEditMode 
        ? `${API_URL}/api/booking-forms/templates/${templateId}`
        : `${API_URL}/api/booking-forms/templates`;
      
      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save template');
      }

      const result = await response.json();
      toast.success(isEditMode ? 'Template updated' : 'Template created');
      
      if (!isEditMode) {
        navigate(`/booking-forms/templates/${result.template_id}`);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!isEditMode) {
      toast.error('Please save the template first');
      return;
    }

    setPublishing(true);
    try {
      const response = await fetch(`${API_URL}/api/booking-forms/templates/${templateId}/publish`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to publish template');
      }

      const result = await response.json();
      toast.success(result.message || 'Template published successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setPublishing(false);
    }
  };

  // Section management
  const addSection = () => {
    const newSection = {
      section_id: generateId('sec'),
      title: 'New Section',
      description: '',
      filled_by: 'CLIENT',
      client_visibility: 'visible',
      order: sections.length + 1,
      fields: []
    };
    setSections([...sections, newSection]);
    setActiveSection(newSection.section_id);
  };

  const updateSection = (sectionId, updates) => {
    setSections(sections.map(s => 
      s.section_id === sectionId ? { ...s, ...updates } : s
    ));
  };

  const deleteSection = (sectionId) => {
    setSections(sections.filter(s => s.section_id !== sectionId));
  };

  const moveSection = (sectionId, direction) => {
    const index = sections.findIndex(s => s.section_id === sectionId);
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === sections.length - 1)) return;
    
    const newSections = [...sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    
    // Update order numbers
    newSections.forEach((s, i) => s.order = i + 1);
    setSections(newSections);
  };

  // Field management
  const addField = (sectionId) => {
    const parentSection = sections.find(s => s.section_id === sectionId);
    setEditingField({
      field_id: generateId('fld'),
      label: '',
      type: 'text',
      required: false,
      filled_by: parentSection?.filled_by === 'OFFICE' ? 'OFFICE' : 'CLIENT',
      client_visibility: 'visible',
      placeholder: '',
      help_text: '',
      options: [],
      validation: {},
      order: (parentSection?.fields.length || 0) + 1,
      _sectionId: sectionId,
      _isNew: true
    });
    setOptionsError('');
    setIsFieldDialogOpen(true);
  };

  const editField = (sectionId, field) => {
    setEditingField({ 
      ...field, 
      options: Array.isArray(field.options) ? field.options : [],
      _sectionId: sectionId, 
      _isNew: false 
    });
    setOptionsError('');
    setIsFieldDialogOpen(true);
  };

  const saveField = () => {
    const { _sectionId, _isNew, ...fieldData } = editingField;
    
    if (!fieldData.label.trim()) {
      toast.error('Field label is required');
      return;
    }

    if (OPTION_TYPES.includes(fieldData.type)) {
      fieldData.options = (fieldData.options || []).map(o => o.trim()).filter(Boolean);
      if (fieldData.options.length === 0) {
        setOptionsError('Add at least one option');
        return;
      }
    } else {
      fieldData.options = [];
    }

    setSections(sections.map(section => {
      if (section.section_id !== _sectionId) return section;
      
      if (_isNew) {
        return { ...section, fields: [...section.fields, fieldData] };
      } else {
        return {
          ...section,
          fields: section.fields.map(f => f.field_id === fieldData.field_id ? fieldData : f)
        };
      }
    }));

    setIsFieldDialogOpen(false);
    setEditingField(null);
  };

  const deleteField = (sectionId, fieldId) => {
    setSections(sections.map(section => {
      if (section.section_id !== sectionId) return section;
      return { ...section, fields: section.fields.filter(f => f.field_id !== fieldId) };
    }));
  };

  // Upload management
  const addUpload = () => {
    setEditingUpload({
      upload_id: generateId('upl'),
      label: '',
      description: '',
      required: true,
      allowed_types: ['jpg', 'jpeg', 'png', 'pdf'],
      max_size_mb: 10,
      _isNew: true
    });
    setIsUploadDialogOpen(true);
  };

  const saveUpload = () => {
    const { _isNew, ...uploadData } = editingUpload;
    
    if (!uploadData.label.trim()) {
      toast.error('Upload label is required');
      return;
    }

    if (_isNew) {
      setRequiredUploads([...requiredUploads, uploadData]);
    } else {
      setRequiredUploads(requiredUploads.map(u => u.upload_id === uploadData.upload_id ? uploadData : u));
    }

    setIsUploadDialogOpen(false);
    setEditingUpload(null);
  };

  const deleteUpload = (uploadId) => {
    setRequiredUploads(requiredUploads.filter(u => u.upload_id !== uploadId));
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="template-builder-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/booking-forms')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {isEditMode ? 'Edit Template' : 'Create Template'}
            </h1>
            <p className="text-slate-500">Build your booking form template</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Draft'}
          </Button>
          {isEditMode && (
            <Button 
              onClick={handlePublish} 
              disabled={publishing}
              className="bg-gold hover:bg-gold/90 text-slate-900"
            >
              <Rocket className="w-4 h-4 mr-2" />
              {publishing ? 'Publishing...' : 'Publish'}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Editor */}
        <div className="lg:col-span-2 space-y-6">
          {/* Template Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Template Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Template Name *</Label>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., Standard Plot Booking Form"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Brief description of this template"
                  rows={2}
                />
              </div>
              <div>
                <Label>Category Tags</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {tags.map(tag => (
                    <Badge
                      key={tag.tag_id}
                      variant={selectedTags.includes(tag.tag_id) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      style={selectedTags.includes(tag.tag_id) ? { backgroundColor: tag.color } : { borderColor: tag.color, color: tag.color }}
                      onClick={() => {
                        if (selectedTags.includes(tag.tag_id)) {
                          setSelectedTags(selectedTags.filter(t => t !== tag.tag_id));
                        } else {
                          setSelectedTags([...selectedTags, tag.tag_id]);
                        }
                      }}
                    >
                      {tag.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sections */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Form Sections</CardTitle>
              <Button size="sm" onClick={addSection}>
                <Plus className="w-4 h-4 mr-1" />
                Add Section
              </Button>
            </CardHeader>
            <CardContent>
              {sections.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p>No sections yet. Add a section to start building your form.</p>
                </div>
              ) : (
                <Accordion type="single" collapsible value={activeSection} onValueChange={setActiveSection}>
                  {sections.map((section, idx) => (
                    <AccordionItem key={section.section_id} value={section.section_id}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2 flex-1">
                          <GripVertical className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{section.title || 'Untitled Section'}</span>
                          <Badge variant="outline" className="ml-2 text-xs">
                            {section.fields.length} fields
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {FILLED_BY_OPTIONS.find(o => o.value === section.filled_by)?.label}
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 pt-4">
                          {/* Section Settings */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Section Title</Label>
                              <Input
                                value={section.title}
                                onChange={(e) => updateSection(section.section_id, { title: e.target.value })}
                              />
                            </div>
                            <div>
                              <Label>Filled By</Label>
                              <Select
                                value={section.filled_by}
                                onValueChange={(value) => updateSection(section.section_id, { filled_by: value })}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {FILLED_BY_OPTIONS.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <Label>Description</Label>
                            <Input
                              value={section.description || ''}
                              onChange={(e) => updateSection(section.section_id, { description: e.target.value })}
                              placeholder="Optional section description"
                            />
                          </div>

                          {/* Fields */}
                          <div className="border rounded-lg p-3">
                            <div className="flex items-center justify-between mb-3">
                              <Label className="text-sm font-medium">Fields</Label>
                              <Button size="sm" variant="outline" onClick={() => addField(section.section_id)}>
                                <Plus className="w-3 h-3 mr-1" />
                                Add Field
                              </Button>
                            </div>
                            {section.fields.length === 0 ? (
                              <p className="text-sm text-slate-500 text-center py-4">No fields in this section</p>
                            ) : (
                              <div className="space-y-2">
                                {section.fields.map(field => (
                                  <div 
                                    key={field.field_id}
                                    className="flex items-center justify-between p-2 bg-slate-50 rounded border"
                                  >
                                    <div className="flex items-center gap-2">
                                      <GripVertical className="w-4 h-4 text-slate-400" />
                                      <span className="text-sm">{field.label}</span>
                                      <Badge variant="outline" className="text-xs">{field.type}</Badge>
                                      {field.required && <Badge className="text-xs bg-red-100 text-red-700">Required</Badge>}
                                    </div>
                                    <div className="flex gap-1">
                                      <Button size="sm" variant="ghost" onClick={() => editField(section.section_id, field)}>
                                        <Settings2 className="w-3 h-3" />
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        className="text-red-500"
                                        onClick={() => deleteField(section.section_id, field.field_id)}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Section Actions */}
                          <div className="flex justify-between pt-2 border-t">
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => moveSection(section.section_id, 'up')} disabled={idx === 0}>
                                <ChevronUp className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => moveSection(section.section_id, 'down')} disabled={idx === sections.length - 1}>
                                <ChevronDown className="w-4 h-4" />
                              </Button>
                            </div>
                            <Button size="sm" variant="outline" className="text-red-500" onClick={() => deleteSection(section.section_id)}>
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete Section
                            </Button>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </CardContent>
          </Card>

          {/* Declaration */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Declaration</CardTitle>
              <CardDescription>Terms and conditions the client must acknowledge</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Declaration Text</Label>
                <Textarea
                  value={declaration.declaration_text}
                  onChange={(e) => setDeclaration({ ...declaration, declaration_text: e.target.value })}
                  placeholder="Enter the declaration text..."
                  rows={6}
                />
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    id="require-ack"
                    checked={declaration.require_ack_checkbox}
                    onCheckedChange={(checked) => setDeclaration({ ...declaration, require_ack_checkbox: checked })}
                  />
                  <Label htmlFor="require-ack">Require acknowledgement checkbox</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="require-sig"
                    checked={declaration.require_signature}
                    onCheckedChange={(checked) => setDeclaration({ ...declaration, require_signature: checked })}
                  />
                  <Label htmlFor="require-sig">Require signature</Label>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Default Link Expiry (days)</Label>
                <Input
                  type="number"
                  value={settings.default_expiry_days}
                  onChange={(e) => setSettings({ ...settings, default_expiry_days: parseInt(e.target.value) || 14 })}
                  min={1}
                  max={90}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="global-branding"
                  checked={settings.use_global_branding}
                  onCheckedChange={(checked) => setSettings({ ...settings, use_global_branding: checked })}
                />
                <Label htmlFor="global-branding">Use company-wide branding</Label>
              </div>
            </CardContent>
          </Card>

          {/* Required Uploads */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Required Documents</CardTitle>
              <Button size="sm" variant="outline" onClick={addUpload}>
                <Plus className="w-3 h-3 mr-1" />
                Add
              </Button>
            </CardHeader>
            <CardContent>
              {requiredUploads.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No document uploads required</p>
              ) : (
                <div className="space-y-2">
                  {requiredUploads.map(upload => (
                    <div key={upload.upload_id} className="flex items-center justify-between p-2 border rounded">
                      <div>
                        <p className="text-sm font-medium">{upload.label}</p>
                        <p className="text-xs text-slate-500">{upload.required ? 'Required' : 'Optional'}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => { setEditingUpload({ ...upload, _isNew: false }); setIsUploadDialogOpen(true); }}
                        >
                          <Settings2 className="w-3 h-3" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-500"
                          onClick={() => deleteUpload(upload.upload_id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Field Edit Dialog */}
      <Dialog open={isFieldDialogOpen} onOpenChange={setIsFieldDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingField?._isNew ? 'Add Field' : 'Edit Field'}</DialogTitle>
          </DialogHeader>
          {editingField && (
            <div className="space-y-4">
              <div>
                <Label>Label *</Label>
                <Input
                  value={editingField.label}
                  onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                  placeholder="Field label"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select
                    value={editingField.type}
                    onValueChange={(value) => setEditingField({ ...editingField, type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Filled By</Label>
                  <Select
                    value={editingField.filled_by}
                    onValueChange={(value) => setEditingField({ ...editingField, filled_by: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FILLED_BY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Placeholder</Label>
                <Input
                  value={editingField.placeholder || ''}
                  onChange={(e) => setEditingField({ ...editingField, placeholder: e.target.value })}
                  placeholder="Optional placeholder text"
                />
              </div>
              <div>
                <Label>Help Text</Label>
                <Input
                  value={editingField.help_text || ''}
                  onChange={(e) => setEditingField({ ...editingField, help_text: e.target.value })}
                  placeholder="Optional help text shown below field"
                />
              </div>
              {OPTION_TYPES.includes(editingField.type) && (
                <OptionListEditor
                  options={editingField.options}
                  onChange={(options) => { setOptionsError(''); setEditingField({ ...editingField, options }); }}
                  error={optionsError}
                />
              )}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="field-required"
                    checked={editingField.required}
                    onCheckedChange={(checked) => setEditingField({ ...editingField, required: checked })}
                  />
                  <Label htmlFor="field-required">Required</Label>
                </div>
                <div>
                  <Select
                    value={editingField.client_visibility}
                    onValueChange={(value) => setEditingField({ ...editingField, client_visibility: value })}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VISIBILITY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFieldDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveField} className="bg-gold hover:bg-gold/90 text-slate-900">
              {editingField?._isNew ? 'Add Field' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Edit Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingUpload?._isNew ? 'Add Document Upload' : 'Edit Document Upload'}</DialogTitle>
          </DialogHeader>
          {editingUpload && (
            <div className="space-y-4">
              <div>
                <Label>Label *</Label>
                <Input
                  value={editingUpload.label}
                  onChange={(e) => setEditingUpload({ ...editingUpload, label: e.target.value })}
                  placeholder="e.g., NID Front Side"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={editingUpload.description || ''}
                  onChange={(e) => setEditingUpload({ ...editingUpload, description: e.target.value })}
                  placeholder="Optional description"
                />
              </div>
              <div>
                <Label>Max File Size (MB)</Label>
                <Input
                  type="number"
                  value={editingUpload.max_size_mb}
                  onChange={(e) => setEditingUpload({ ...editingUpload, max_size_mb: parseInt(e.target.value) || 10 })}
                  min={1}
                  max={50}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="upload-required"
                  checked={editingUpload.required}
                  onCheckedChange={(checked) => setEditingUpload({ ...editingUpload, required: checked })}
                />
                <Label htmlFor="upload-required">Required</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUploadDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveUpload} className="bg-gold hover:bg-gold/90 text-slate-900">
              {editingUpload?._isNew ? 'Add Upload' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
